<template>
    <div>
        <v-alert v-if="!scenario" type="info" text>Selecione um cenário para consultar os documentos.</v-alert>
        <template v-else>
            <div class="d-flex flex-wrap align-center justify-space-between mb-4" style="gap: 12px;">
                <div>
                    <h3 class="ui-section-title">Documentos e fundamentos</h3>
                    <p class="ui-section-description">
                        Os documentos pertencem exclusivamente ao cenário {{ scenario.name }}.
                    </p>
                </div>
                <v-btn color="primary" depressed @click="openForm()">
                    <v-icon left small>mdi-paperclip-plus</v-icon>
                    Anexar documento
                </v-btn>
            </div>

            <ui-table-card clearable :can-clear="hasFilters" @clear="clearFilters">
                <template #toolbar>
                    <v-text-field v-model="filters.search" dense outlined hide-details clearable
                        prepend-inner-icon="mdi-magnify" placeholder="Pesquisar documento, descrição ou crédito"
                        class="ui-toolbar__search" @input="filterDebounced" @keyup.enter="applyFilters" />
                    <v-select v-model="filters.category" :items="categories" item-text="text" item-value="value"
                        dense outlined hide-details clearable placeholder="Todas as categorias" class="ui-toolbar__field"
                        :menu-props="{ offsetY: true }" @change="applyFilters" />
                    <v-select v-model="filters.binding_type" :items="bindingTypes" item-text="text" item-value="value"
                        dense outlined hide-details clearable placeholder="Todos os vínculos" class="ui-toolbar__field"
                        :menu-props="{ offsetY: true }" @change="applyFilters" />
                    <v-btn icon :loading="loading" title="Atualizar" @click="load"><v-icon>mdi-refresh</v-icon></v-btn>
                </template>

                <v-data-table :headers="headers" :items="documents" :loading="loading" disable-sort hide-default-footer
                    :page.sync="page" :items-per-page="itemsPerPage" class="ui-clickable-rows"
                    no-data-text="Nenhum documento encontrado neste cenário" loading-text="Carregando dados..." @click:row="openDetails">
                    <template v-slot:[`item.name`]='{ item }'>
                        <div class="py-2">
                            <div class="ui-cell-main">{{ item.name }}</div>
                            <div class="ui-cell-sub">{{ item.original_name || item.external_link || "Sem arquivo" }}</div>
                        </div>
                    </template>
                    <template v-slot:[`item.binding`]='{ item }'>
                        <ui-badge :text="bindingName(item.binding_type)" tone="neutral" />
                        <div v-if="item.credit_reference" class="ui-cell-sub">{{ item.credit_reference }}</div>
                    </template>
                    <template v-slot:[`item.active`]='{ item }'>
                        <ui-badge :text="Number(item.active) === 1 ? 'Ativo' : 'Desativado'" :tone="Number(item.active) === 1 ? 'success' : 'neutral'" />
                    </template>
                    <template v-slot:[`item.actions`]='{ item }'>
                        <ui-row-actions :actions="[
                            { text: 'Visualizar', icon: 'mdi-eye-outline', handler: () => openDetails(item) },
                            { text: 'Baixar', icon: 'mdi-download-outline', hidden: !item.stored_name, handler: () => download(item) },
                            { text: 'Histórico', icon: 'mdi-history', handler: () => openHistory(item) },
                            { text: 'Desativar', icon: 'mdi-archive-arrow-down-outline', danger: true, hidden: Number(item.active) !== 1, handler: () => openDeactivate(item) },
                            { text: 'Restaurar', icon: 'mdi-archive-arrow-up-outline', hidden: Number(item.active) === 1, handler: () => restore(item) },
                        ]" />
                    </template>
                </v-data-table>

                <template #footer>
                    <ui-pagination :total="documents.length" :page.sync="page" :items-per-page.sync="itemsPerPage" />
                </template>
            </ui-table-card>
        </template>

        <v-dialog v-model="formDialog" max-width="760px" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header"><h3 class="ui-section-title">{{ form.id ? "Editar documento" : "Anexar documento" }}</h3></div>
                <div class="ui-card__body">
                    <v-row dense>
                        <v-col cols="12" md="7"><v-text-field v-model="form.name" dense outlined label="Nome do documento" /></v-col>
                        <v-col cols="12" md="5"><v-text-field v-model="form.document_date" dense outlined type="date" label="Data do documento" /></v-col>
                        <v-col cols="12" md="6"><v-select v-model="form.category" :items="categories" item-text="text" item-value="value" dense outlined label="Categoria" /></v-col>
                        <v-col cols="12" md="6"><v-select v-model="form.binding_type" :items="bindingTypes" item-text="text" item-value="value" dense outlined label="Tipo de vínculo" /></v-col>
                        <v-col v-if="requiresCredit" cols="12">
                            <v-select v-model="form.credit_reference" :items="creditOptions" item-text="label" item-value="value"
                                dense outlined clearable label="Crédito relacionado" />
                        </v-col>
                        <v-col cols="12"><v-file-input v-if="!form.id" v-model="form.file" dense outlined show-size label="Arquivo" /></v-col>
                        <v-col cols="12"><v-text-field v-model="form.external_link" dense outlined label="Link externo (opcional)" /></v-col>
                        <v-col cols="12"><v-textarea v-model="form.description" dense outlined rows="3" label="Descrição ou fundamento" /></v-col>
                    </v-row>
                </div>
                <div class="ui-card__footer">
                    <v-btn text :disabled="saving" @click="formDialog = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('save')" color="primary" depressed :loading="saving" @click="save">Salvar documento</v-btn>
                </div>
            </v-card>
        </v-dialog>

        <v-dialog v-model="detailDialog" max-width="760px">
            <v-card v-if="selected" class="ui-card">
                <div class="ui-card__header"><h3 class="ui-section-title">{{ selected.name }}</h3></div>
                <div class="ui-card__body">
                    <ui-badge :text="categoryName(selected.category)" tone="neutral" class="mr-2" />
                    <ui-badge :text="bindingName(selected.binding_type)" tone="neutral" />
                    <div class="mt-4"><strong>Crédito:</strong> {{ selected.credit_reference || "Cenário inteiro" }}</div>
                    <div class="mt-2"><strong>Data:</strong> {{ selected.document_date || "Não informada" }}</div>
                    <div class="mt-2"><strong>Responsável:</strong> {{ selected.created_by_name || "Sistema" }}</div>
                    <div class="mt-2"><strong>Hash SHA-256:</strong> <code>{{ selected.file_hash || "Não aplicável" }}</code></div>
                    <div class="mt-3">{{ selected.description || "Sem descrição." }}</div>
                    <v-alert v-if="Number(selected.active) !== 1" type="warning" text dense class="mt-4">
                        Documento desativado: {{ selected.deactivation_reason }}
                    </v-alert>
                </div>
                <div class="ui-card__footer">
                    <v-btn text @click="detailDialog = false">Fechar</v-btn>
                    <v-btn v-if="selected.stored_name" outlined color="primary" @click="download(selected)">Baixar</v-btn>
                    <v-btn v-if="$allowed('edit')" color="primary" depressed @click="openForm(selected)">Editar metadados</v-btn>
                </div>
            </v-card>
        </v-dialog>

        <v-dialog v-model="historyDialog" max-width="720px">
            <v-card class="ui-card">
                <div class="ui-card__header"><h3 class="ui-section-title">Histórico do documento</h3></div>
                <div class="ui-card__body">
                    <v-timeline dense align-top>
                        <v-timeline-item v-for="log in history" :key="log.id" small color="primary">
                            <strong>{{ actionName(log.action) }}</strong>
                            <div class="ui-small ui-muted">{{ formatDate(log.created_at) }} · {{ log.admin_name || "Sistema" }}</div>
                        </v-timeline-item>
                    </v-timeline>
                </div>
            </v-card>
        </v-dialog>

        <v-dialog v-model="deactivateDialog" max-width="560px">
            <v-card class="ui-card">
                <div class="ui-card__header"><h3 class="ui-section-title">Desativar documento</h3></div>
                <div class="ui-card__body">
                    <v-alert type="warning" text dense>O arquivo e o histórico serão preservados.</v-alert>
                    <v-textarea v-model="deactivationReason" dense outlined rows="3" label="Justificativa obrigatória" />
                </div>
                <div class="ui-card__footer">
                    <v-btn text @click="deactivateDialog = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('edit')" color="error" depressed :disabled="!deactivationReason.trim()" @click="deactivate">Desativar</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import _ from "lodash";

const INITIAL_FILTERS = () => ({ search: "", category: null, binding_type: null });

export default {
    name: "ScenarioDocuments",
    props: { scenario: Object, credits: { type: Array, default: () => [] } },
    data: () => ({
        loading: false, saving: false, documents: [], selected: null, history: [],
        formDialog: false, detailDialog: false, historyDialog: false, deactivateDialog: false,
        deactivationReason: "", filters: INITIAL_FILTERS(), page: 1, itemsPerPage: 10,
        form: {},
        headers: [
            { text: "Documento", value: "name" }, { text: "Categoria", value: "category" },
            { text: "Vínculo", value: "binding" }, { text: "Responsável", value: "created_by_name" },
            { text: "Situação", value: "active" }, { text: "", value: "actions", sortable: false, align: "end", width: 56 },
        ],
        categories: [
            ["decisao_judicial", "Decisão judicial"], ["qgc", "QGC ou relação de credores"],
            ["manifestacao", "Manifestação"], ["parecer_juridico", "Parecer jurídico"], ["laudo", "Laudo"],
            ["ata", "Ata"], ["planilha", "Planilha"], ["comprovante", "Comprovante"], ["outro", "Outro"],
        ].map(([value, text]) => ({ value, text })),
        bindingTypes: [
            ["scenario", "Cenário inteiro"], ["credit", "Crédito específico"], ["credit_inclusion", "Inclusão de crédito"],
            ["credit_disregard", "Exclusão ou desconsideração"], ["class_change", "Alteração de classe"],
            ["amount_change", "Alteração de valor"], ["currency_change", "Alteração de moeda ou cotação"],
            ["voting_right_change", "Alteração do direito de voto"], ["pending", "Pendência"],
            ["result", "Resultado ou apuração"], ["other", "Outro"],
        ].map(([value, text]) => ({ value, text })),
    }),
    computed: {
        hasFilters() { return Object.values(this.filters).some((value) => value); },
        requiresCredit() { return !["scenario", "result"].includes(this.form.binding_type); },
        creditOptions() { return this.credits.map((item) => ({ value: item.credit_reference, label: `${item.name} · ${item.credit_reference || "sem referência"}` })); },
    },
    watch: { scenario: { immediate: true, handler(value) { this.documents = []; if (value) this.load(); } } },
    methods: {
        applyFilters() { this.page = 1; this.load(); },
        filterDebounced: _.debounce(function () { this.applyFilters(); }, 400),
        clearFilters() { this.filters = INITIAL_FILTERS(); this.applyFilters(); },
        async load() {
            if (!this.scenario) return;
            this.loading = true;
            try { const r = await axios.get(`/api/admin/general-meeting-scenarios/${this.scenario.id}/documents`, { params: this.filters }); this.documents = r.data.content || []; }
            catch (e) { this.notifyError(e); } finally { this.loading = false; }
        },
        openForm(item = null) {
            this.form = item ? { ...item, file: null } : { name: "", category: "outro", binding_type: "scenario", credit_reference: null, document_date: null, external_link: "", description: "", file: null };
            this.detailDialog = false; this.formDialog = true;
        },
        async save() {
            if (!this.form.name || !this.form.category || !this.form.binding_type) return this.$dialog.notify.error("Preencha nome, categoria e tipo de vínculo.");
            this.saving = true;
            try {
                if (this.form.id) await axios.patch(`/api/admin/general-meeting-scenario-documents/${this.form.id}`, this.form);
                else {
                    const data = new FormData();
                    Object.keys(this.form).forEach((key) => { if (this.form[key] !== null && this.form[key] !== undefined) data.append(key, this.form[key]); });
                    await axios.post(`/api/admin/general-meeting-scenarios/${this.scenario.id}/documents`, data);
                }
                this.formDialog = false; await this.load(); this.$dialog.notify.info("Documento salvo e registrado na auditoria.", { position: "top-right" });
            } catch (e) { this.notifyError(e); } finally { this.saving = false; }
        },
        async openDetails(item) { const r = await axios.get(`/api/admin/general-meeting-scenario-documents/${item.id}`); this.selected = r.data.content; this.detailDialog = true; },
        async openHistory(item) { const r = await axios.get(`/api/admin/general-meeting-scenario-documents/${item.id}`); this.history = r.data.content.logs || []; this.historyDialog = true; },
        openDeactivate(item) { this.selected = item; this.deactivationReason = ""; this.deactivateDialog = true; },
        async deactivate() { await axios.patch(`/api/admin/general-meeting-scenario-documents/${this.selected.id}/deactivate`, { reason: this.deactivationReason }); this.deactivateDialog = false; await this.load(); },
        async restore(item) { await axios.patch(`/api/admin/general-meeting-scenario-documents/${item.id}/restore`); await this.load(); },
        async download(item) {
            try {
                const response = await axios.get(`/api/admin/general-meeting-scenario-documents/${item.id}/download`, { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                link.download = item.original_name || item.name || "documento";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } catch (error) { this.notifyError(error); }
        },
        categoryName(value) { const item = this.categories.find((x) => x.value === value); return item ? item.text : value; },
        bindingName(value) { const item = this.bindingTypes.find((x) => x.value === value); return item ? item.text : value; },
        actionName(value) { return ({ created: "Documento anexado", updated: "Metadados alterados", deactivated: "Documento desativado", restored: "Documento restaurado" })[value] || value; },
        formatDate(value) { return value ? this.$options.filters.defaultDate(value, "DD/MM/YYYY HH:mm [BRT]") : "—"; },
        notifyError(error) { const message = error.response && error.response.data ? error.response.data.message || error.response.data.error : error.message; this.$dialog.notify.error(message || "Não foi possível concluir a operação.", { position: "top-right" }); },
    },
};
</script>
